/**
 * Modelo de Maturidade TPL — 5 pilares (Disciplina, Conformidade, Escalonamento, Sustentação, Resultados).
 * Agrega métricas já existentes no sistema; não altera regras de negócio.
 */

import type { EnterpriseAction } from '../../../services/actionService';
import type { Indicator, IndicatorSnapshot } from '../../../types/indicator';
import type { LstExecucao, LstTemplate } from '../types';
import {
  buildParetoFromExecucoes,
} from './adherenceLossDiagnostic';
import {
  aggregateResultScores,
  computeExecutionResultScore,
  computeIndicatorResultScore,
  pickLinkedIndicators,
} from './lstCorrelation';
import { computeTplOeeMetrics } from './lstOeeMetrics';
import { isTemplateDueOnDate } from './lstSchedule';
import { lastMonthsKeys, toLocalISODate } from './lstService';

export type TplMaturityBand = 'excelente' | 'maduro' | 'emergente' | 'critico';

export type TplMaturityPillarKey =
  | 'disciplina'
  | 'conformidade'
  | 'escalonamento'
  | 'sustentacao'
  | 'resultados';

export interface TplMaturityPillars {
  disciplina: number;
  conformidade: number;
  escalonamento: number;
  sustentacao: number;
  /** null quando não há indicadores vinculados ou dados no período. */
  resultados: number | null;
}

export interface TplEvolutionPoint {
  label: string;
  pct: number;
}

export interface TplMaturityPillarDetail {
  key: TplMaturityPillarKey;
  label: string;
  question: string;
  score: number | null;
  trend?: number;
  weeklyEvolution?: TplEvolutionPoint[];
  monthlyEvolution?: TplEvolutionPoint[];
  highlights: string[];
}

export interface TplMaturityEscalationStats {
  desviosAbertos: number;
  desviosFechados: number;
  acoesAbertas: number;
  acoesVencidas: number;
}

export interface TplMaturityModel {
  score: number;
  band: TplMaturityBand;
  bandLabel: string;
  pillars: TplMaturityPillars;
  pillarDetails: Record<TplMaturityPillarKey, TplMaturityPillarDetail>;
  escalation: TplMaturityEscalationStats;
  insights: string[];
  risks: string[];
  recommendedActions: string[];
}

export interface ComputeTplMaturityInput {
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  weekDates: string[];
  monthDates: string[];
  /** Últimos ~90 dias para sustentação; default = derivado de execucoes/today. */
  sustainDates?: string[];
  today?: string;
  leaders?: Array<{ userId: string; name: string }>;
  indicators?: Indicator[];
  indicatorSnapshots?: IndicatorSnapshot[];
  correctiveActions?: EnterpriseAction[];
}

const PILLAR_META: Record<
  TplMaturityPillarKey,
  { label: string; question: string }
> = {
  disciplina: {
    label: 'Disciplina',
    question: 'O líder compareceu à rotina?',
  },
  conformidade: {
    label: 'Conformidade',
    question: 'O líder executou corretamente?',
  },
  escalonamento: {
    label: 'Escalonamento',
    question: 'O sistema reage aos problemas?',
  },
  sustentacao: {
    label: 'Sustentação',
    question: 'O padrão está sendo sustentado?',
  },
  resultados: {
    label: 'Resultados',
    question: 'As rotinas estão melhorando o negócio?',
  },
};

function bandFromScore(score: number): { band: TplMaturityBand; bandLabel: string } {
  if (score >= 90) return { band: 'excelente', bandLabel: 'Excelência' };
  if (score >= 75) return { band: 'maduro', bandLabel: 'Maduro' };
  if (score >= 55) return { band: 'emergente', bandLabel: 'Emergente' };
  return { band: 'critico', bandLabel: 'Crítico' };
}

function stdDev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

function chunkDates(dates: string[], size: number): string[][] {
  const sorted = [...dates].sort();
  const chunks: string[][] = [];
  for (let i = 0; i < sorted.length; i += size) {
    chunks.push(sorted.slice(i, i + size));
  }
  return chunks.filter((c) => c.length > 0);
}

function previousWeekDates(weekDates: string[]): string[] {
  if (weekDates.length === 0) return [];
  const [y, m, d] = weekDates[0].split('-').map(Number);
  const start = new Date(y, m - 1, d);
  start.setDate(start.getDate() - 7);
  const out: string[] = [];
  for (let i = 0; i < weekDates.length; i++) {
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    out.push(toLocalISODate(dt));
  }
  return out;
}

export function lastNDaysDates(n: number, endDate: string): string[] {
  const [y, m, d] = endDate.split('-').map(Number);
  const end = new Date(y, m - 1, d);
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const dt = new Date(end);
    dt.setDate(end.getDate() - i);
    out.push(toLocalISODate(dt));
  }
  return out;
}

function isDeviationExec(e: LstExecucao): boolean {
  return (
    e.status === 'realizado_com_desvio' ||
    (e.status === 'nao_realizado' && !!e.justificativa_desvio?.trim())
  );
}

function filterOrgLstCorrectiveActions(actions: EnterpriseAction[]): EnterpriseAction[] {
  return actions.filter((a) => {
    if (a.status === 'completed' || a.status === 'cancelled') return false;
    const meta = a.metadata ?? {};
    const source = meta.source as string | undefined;
    if (source === 'lst' || source === 'lst_tpl') return true;
    const title = a.title.toLowerCase();
    return title.includes('tpl') || title.includes('aderência');
  });
}

function isOverdueAction(a: EnterpriseAction): boolean {
  return (
    a.sla_status === 'overdue' ||
    a.sla_status === 'critical_director' ||
    a.sla_status === 'andon_triggered'
  );
}

function datesInMonthKey(key: string): string[] {
  const [yy, mm] = key.split('-').map(Number);
  const total = new Date(yy, mm, 0).getDate();
  return Array.from({ length: total }, (_, i) =>
    toLocalISODate(new Date(yy, mm - 1, i + 1)),
  );
}

function buildSixMonthDisciplinaEvolution(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  today: string,
): TplEvolutionPoint[] {
  return lastMonthsKeys(6).map(({ key, label }) => {
    const dates = datesInMonthKey(key).filter((d) => d <= today);
    const oee = dates.length > 0 ? computeTplOeeMetrics(templates, execucoes, dates) : null;
    return { label, pct: oee?.disciplinaPct ?? 0 };
  });
}

function buildMonthlyDisciplinaEvolution(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  monthDates: string[],
): TplEvolutionPoint[] {
  const byMonth = new Map<string, string[]>();
  for (const date of monthDates) {
    const key = date.slice(0, 7);
    const arr = byMonth.get(key) ?? [];
    arr.push(date);
    byMonth.set(key, arr);
  }
  return [...byMonth.entries()].map(([key, dates]) => {
    const oee = computeTplOeeMetrics(templates, execucoes, dates);
    const [yy, mm] = key.split('-').map(Number);
    const label = new Date(yy, mm - 1, 1)
      .toLocaleDateString('pt-BR', { month: 'short' })
      .replace('.', '');
    return { label, pct: oee.disciplinaPct };
  });
}

function buildWeeklyDisciplinaEvolution(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  weekDates: string[],
): TplEvolutionPoint[] {
  return weekDates.map((date) => {
    const oee = computeTplOeeMetrics(templates, execucoes, [date]);
    const [, m, d] = date.split('-');
    return { label: `${d}/${m}`, pct: oee.disciplinaPct };
  });
}

function topProblematicRoutines(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  dates: string[],
  limit = 3,
): Array<{ title: string; desvios: number }> {
  const counts = new Map<string, { title: string; desvios: number }>();
  for (const date of dates) {
    const due = templates.filter((t) => t.active && isTemplateDueOnDate(t, date));
    for (const tpl of due) {
      const ex = execucoes.find((e) => e.template_id === tpl.id && e.data === date);
      if (!ex || ex.status !== 'realizado_com_desvio') continue;
      const cur = counts.get(tpl.id) ?? { title: tpl.title, desvios: 0 };
      cur.desvios += 1;
      counts.set(tpl.id, cur);
    }
  }
  return [...counts.values()].sort((a, b) => b.desvios - a.desvios).slice(0, limit);
}

function computeResultadosScore(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  indicators: Indicator[],
  indicatorSnapshots: IndicatorSnapshot[],
  leaders: Array<{ userId: string; name: string }>,
  periodStart: string,
  periodEnd: string,
): { score: number | null; highlights: string[] } {
  const linked = pickLinkedIndicators(templates, indicators);
  if (linked.length === 0) {
    return { score: null, highlights: ['Nenhum indicador vinculado às rotinas.'] };
  }

  const scores = [];

  for (const ind of linked) {
    const snapScore = computeIndicatorResultScore(
      ind,
      indicatorSnapshots,
      periodStart,
      periodEnd,
    );
    if (snapScore.achievementPct != null) scores.push(snapScore);
  }

  for (const leader of leaders) {
    const execScore = computeExecutionResultScore(
      templates,
      execucoes,
      indicators,
      leader.userId,
      periodStart,
      periodEnd,
    );
    if (execScore?.achievementPct != null) scores.push(execScore);
  }

  const agg = aggregateResultScores(scores);
  const highlights = linked.slice(0, 3).map((ind) => {
    const tpl = templates.find((t) => t.linked_indicator_id === ind.id);
    return tpl ? `${tpl.title} → ${ind.name}` : ind.name;
  });

  if (agg?.achievementPct == null) {
    return {
      score: null,
      highlights: [
        ...highlights,
        'Informe valores realizados no gemba ou registre medições dos indicadores.',
      ],
    };
  }

  return { score: agg.achievementPct, highlights };
}

function computeReincidenceLeader(
  execucoes: LstExecucao[],
  leaders: Array<{ userId: string; name: string }>,
  dates: string[],
): string | null {
  const inPeriod = execucoes.filter(
    (e) => e.data >= dates[0] && e.data <= dates[dates.length - 1] && isDeviationExec(e),
  );
  if (inPeriod.length === 0 || leaders.length === 0) return null;

  const byUser = new Map<string, number>();
  for (const e of inPeriod) {
    byUser.set(e.user_id, (byUser.get(e.user_id) ?? 0) + 1);
  }

  let worst: { name: string; count: number } | null = null;
  for (const leader of leaders) {
    const count = byUser.get(leader.userId) ?? 0;
    if (!worst || count > worst.count) worst = { name: leader.name, count };
  }
  if (!worst || worst.count < 2) return null;
  return worst.name;
}

export function computeTplMaturityModel(input: ComputeTplMaturityInput): TplMaturityModel {
  const {
    templates,
    execucoes,
    weekDates,
    monthDates,
    today = toLocalISODate(new Date()),
    leaders = [],
    indicators = [],
    indicatorSnapshots = [],
    correctiveActions = [],
  } = input;

  const sustainDates =
    input.sustainDates ??
    (monthDates.length >= 28
      ? monthDates.slice(-90)
      : lastNDaysDates(90, today));

  const activeTemplates = templates.filter((t) => t.active);
  const weekOee = computeTplOeeMetrics(activeTemplates, execucoes, weekDates);
  const prevWeekDates = previousWeekDates(weekDates);
  const prevWeekOee =
    prevWeekDates.length > 0
      ? computeTplOeeMetrics(activeTemplates, execucoes, prevWeekDates)
      : null;

  const disciplinaTrend =
    prevWeekOee != null ? weekOee.disciplinaPct - prevWeekOee.disciplinaPct : undefined;

  const weeklyDisciplina = buildWeeklyDisciplinaEvolution(activeTemplates, execucoes, weekDates);
  const monthlyDisciplina =
    monthDates.length > 31
      ? buildMonthlyDisciplinaEvolution(activeTemplates, execucoes, monthDates)
      : buildSixMonthDisciplinaEvolution(activeTemplates, execucoes, today);

  const conformidadeTrend =
    prevWeekOee != null ? weekOee.conformidadePct - prevWeekOee.conformidadePct : undefined;

  const templateTitles = new Map(activeTemplates.map((t) => [t.id, t.title]));
  const weekDeviations = execucoes.filter(
    (e) => weekDates.includes(e.data) && isDeviationExec(e),
  );
  const desviosFechados = weekDeviations.filter((e) => !!e.action_id).length;
  const desviosAbertos = weekDeviations.length - desviosFechados;

  const orgActions = filterOrgLstCorrectiveActions(correctiveActions);
  const acoesVencidas = orgActions.filter(isOverdueAction).length;

  const tratamentoPct =
    weekDeviations.length === 0
      ? 100
      : Math.round((desviosFechados / weekDeviations.length) * 100);

  const slaPct =
    orgActions.length === 0
      ? 100
      : Math.max(0, 100 - Math.round((acoesVencidas / orgActions.length) * 100));

  const escalonamentoScore = Math.round((tratamentoPct + slaPct) / 2);

  const sustainWeeks = chunkDates(sustainDates, 7);
  const weeklyDisciplinaSustain = sustainWeeks.map((w) =>
    computeTplOeeMetrics(activeTemplates, execucoes, w).disciplinaPct,
  );
  const weeklyConformidadeSustain = sustainWeeks.map((w) =>
    computeTplOeeMetrics(activeTemplates, execucoes, w).conformidadePct,
  );

  const stabilityDisc = Math.max(0, 100 - Math.round(stdDev(weeklyDisciplinaSustain) * 3));
  const stabilityConf = Math.max(0, 100 - Math.round(stdDev(weeklyConformidadeSustain) * 3));

  const desvioEvents = execucoes.filter(
    (e) =>
      e.data >= sustainDates[0] &&
      e.data <= sustainDates[sustainDates.length - 1] &&
      isDeviationExec(e),
  );
  const desvioByTemplate = new Map<string, Set<string>>();
  for (const e of desvioEvents) {
    const set = desvioByTemplate.get(e.template_id) ?? new Set<string>();
    set.add(e.data);
    desvioByTemplate.set(e.template_id, set);
  }
  const reincidentEvents = [...desvioByTemplate.values()].filter((s) => s.size >= 2).length;
  const reincidenceScore =
    desvioEvents.length === 0
      ? 100
      : Math.max(0, 100 - Math.round((reincidentEvents / desvioEvents.length) * 100));

  const mid = Math.floor(sustainDates.length / 2);
  const firstHalf = sustainDates.slice(0, mid);
  const secondHalf = sustainDates.slice(mid);
  const firstDisc =
    firstHalf.length > 0
      ? computeTplOeeMetrics(activeTemplates, execucoes, firstHalf).disciplinaPct
      : weekOee.disciplinaPct;
  const secondDisc =
    secondHalf.length > 0
      ? computeTplOeeMetrics(activeTemplates, execucoes, secondHalf).disciplinaPct
      : weekOee.disciplinaPct;
  const trend90 = secondDisc - firstDisc;
  const trendBonus = Math.max(-10, Math.min(10, trend90));
  const sustentacaoScore = Math.round(
    (stabilityDisc + stabilityConf + reincidenceScore + 50 + trendBonus) / 4,
  );

  const periodStart = monthDates[0] ?? weekDates[0] ?? sustainDates[0];
  const periodEnd = monthDates[monthDates.length - 1] ?? weekDates[weekDates.length - 1] ?? today;
  const { score: resultadosScore, highlights: resultadosHighlights } = computeResultadosScore(
    activeTemplates,
    execucoes,
    indicators,
    indicatorSnapshots,
    leaders,
    periodStart,
    periodEnd,
  );

  const pareto = buildParetoFromExecucoes(
    execucoes.filter((e) => weekDates.includes(e.data)),
    templateTitles,
  );
  const topLoss = pareto.slice(0, 2);
  const problematic = topProblematicRoutines(activeTemplates, execucoes, weekDates);

  const conformidadeHighlights: string[] = [];
  if (topLoss.length > 0) {
    conformidadeHighlights.push(
      `${topLoss[0].label}: ${topLoss[0].count} ocorrência(s) (${topLoss[0].pct}%)`,
    );
  }
  for (const r of problematic) {
    conformidadeHighlights.push(`${r.title}: ${r.desvios} desvio(s) na semana`);
  }
  if (conformidadeHighlights.length === 0 && weekOee.counts.desvio === 0) {
    conformidadeHighlights.push('Nenhuma não conformidade registrada na semana.');
  }

  const escalonamentoHighlights: string[] = [
    `${desviosFechados} desvio(s) tratado(s) · ${desviosAbertos} aberto(s)`,
  ];
  if (orgActions.length > 0) {
    escalonamentoHighlights.push(
      `${orgActions.length} ação(ões) corretiva(s) aberta(s) · ${acoesVencidas} vencida(s)`,
    );
  }

  const sustentacaoHighlights: string[] = [
    `Estabilidade disciplina: ${stabilityDisc}% · conformidade: ${stabilityConf}%`,
    `Reincidência de desvios (90d): ${reincidentEvents} rotina(s)`,
  ];
  if (trend90 >= 5) sustentacaoHighlights.push(`Tendência positiva: +${trend90} p.p. em 90 dias.`);
  else if (trend90 <= -5) {
    sustentacaoHighlights.push(`Tendência negativa: ${trend90} p.p. em 90 dias.`);
  }

  const disciplinaHighlights: string[] = [
    `${weekOee.counts.executadas}/${weekOee.counts.planned} rotinas executadas na semana`,
  ];
  if (disciplinaTrend != null && Math.abs(disciplinaTrend) >= 3) {
    disciplinaHighlights.push(
      disciplinaTrend > 0
        ? `+${disciplinaTrend} p.p. vs. semana anterior`
        : `${disciplinaTrend} p.p. vs. semana anterior`,
    );
  }

  const pillars: TplMaturityPillars = {
    disciplina: weekOee.disciplinaPct,
    conformidade: weekOee.conformidadePct,
    escalonamento: escalonamentoScore,
    sustentacao: Math.min(100, Math.max(0, sustentacaoScore)),
    resultados: resultadosScore,
  };

  const pillarScores = [
    pillars.disciplina,
    pillars.conformidade,
    pillars.escalonamento,
    pillars.sustentacao,
    ...(pillars.resultados != null ? [pillars.resultados] : []),
  ];
  const score = Math.round(pillarScores.reduce((s, v) => s + v, 0) / pillarScores.length);
  const { band, bandLabel } = bandFromScore(score);

  const pillarDetails: Record<TplMaturityPillarKey, TplMaturityPillarDetail> = {
    disciplina: {
      key: 'disciplina',
      ...PILLAR_META.disciplina,
      score: pillars.disciplina,
      trend: disciplinaTrend,
      weeklyEvolution: weeklyDisciplina,
      monthlyEvolution: monthlyDisciplina,
      highlights: disciplinaHighlights,
    },
    conformidade: {
      key: 'conformidade',
      ...PILLAR_META.conformidade,
      score: pillars.conformidade,
      trend: conformidadeTrend,
      highlights: conformidadeHighlights,
    },
    escalonamento: {
      key: 'escalonamento',
      ...PILLAR_META.escalonamento,
      score: pillars.escalonamento,
      highlights: escalonamentoHighlights,
    },
    sustentacao: {
      key: 'sustentacao',
      ...PILLAR_META.sustentacao,
      score: pillars.sustentacao,
      highlights: sustentacaoHighlights,
    },
    resultados: {
      key: 'resultados',
      ...PILLAR_META.resultados,
      score: pillars.resultados,
      highlights: resultadosHighlights,
    },
  };

  const insights: string[] = [];
  const risks: string[] = [];
  const recommendedActions: string[] = [];

  if (pillars.disciplina >= 85 && pillars.conformidade < 70) {
    insights.push('Disciplina elevada, porém conformidade abaixo da meta.');
    recommendedActions.push('Priorize auditoria de campo nas rotinas com maior índice de desvio.');
  }

  if (topLoss.length >= 2) {
    const pct = topLoss[0].pct + topLoss[1].pct;
    insights.push(`${pct}% das perdas estão concentradas em ${topLoss[0].label} e ${topLoss[1].label}.`);
  } else if (topLoss.length === 1 && topLoss[0].pct >= 30) {
    insights.push(`${topLoss[0].pct}% das perdas concentram-se em ${topLoss[0].label}.`);
  }

  const reincidenceLeader = computeReincidenceLeader(execucoes, leaders, sustainDates);
  if (reincidenceLeader) {
    insights.push(`${reincidenceLeader} possui maior reincidência de desvios.`);
    recommendedActions.push(`Realizar gemba de suporte com ${reincidenceLeader}.`);
  }

  if (acoesVencidas > 0) {
    insights.push('Escalonamentos vencidos estão impactando a sustentação.');
    risks.push(`${acoesVencidas} ação(ões) corretiva(s) com SLA vencido.`);
    recommendedActions.push('Escalonar ações vencidas na reunião de camada.');
  }

  if (desviosAbertos > 0) {
    risks.push(`${desviosAbertos} desvio(s) sem plano corretivo vinculado.`);
  }

  if (pillars.disciplina < 70) {
    risks.push('Disciplina abaixo de 70% — rotinas planejadas não estão sendo executadas.');
    recommendedActions.push('Revisar carga do planner e conflitos de agenda dos líderes.');
  }

  if (pillars.conformidade < 70 && weekOee.counts.desvio > 0) {
    risks.push('Conformidade crítica — execuções fora do padrão esperado.');
  }

  if (pillars.sustentacao < 60) {
    risks.push('Padrão instável nos últimos 90 dias.');
  }

  if (pillars.resultados != null && pillars.resultados < 70 && pillars.disciplina >= 75) {
    insights.push('Rotinas executadas, mas indicadores operacionais abaixo da meta.');
    recommendedActions.push('Validar vínculos rotina → indicador e metas nas configurações.');
  }

  if (insights.length === 0 && risks.length === 0) {
    insights.push('Maturidade equilibrada nos cinco pilares — mantenha o ritmo de gemba e camada.');
  }

  return {
    score,
    band,
    bandLabel,
    pillars,
    pillarDetails,
    escalation: {
      desviosAbertos,
      desviosFechados,
      acoesAbertas: orgActions.length,
      acoesVencidas,
    },
    insights,
    risks,
    recommendedActions,
  };
}

/** Pontuação resumida para painéis compactos (ex.: auditoria de camada). */
export function computeTplMaturitySummary(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  weekDates: string[],
): Pick<TplMaturityModel, 'score' | 'band' | 'bandLabel' | 'pillars'> {
  const model = computeTplMaturityModel({ templates, execucoes, weekDates, monthDates: weekDates });
  return {
    score: model.score,
    band: model.band,
    bandLabel: model.bandLabel,
    pillars: model.pillars,
  };
}
